import { useEffect, useState, type RefObject } from 'react';

/** The on-screen keyboard is taken to be open when it hides more than this. */
const KEYBOARD_PX = 120;
/**
 * When to look again after the focus moves. An iPhone reports the viewport's
 * new size late, in steps, and sometimes not at all once the keyboard has gone.
 */
const SETTLE_MS = [60, 250, 500, 900];

/** Whether the keyboard has a reason to be up: something that is typed into holds the focus. */
export function typing(): boolean {
  const active = document.activeElement;
  if (active instanceof HTMLTextAreaElement) return true;
  if (active instanceof HTMLInputElement) {
    return !['button', 'checkbox', 'radio', 'file', 'range', 'color', 'submit', 'reset'].includes(
      active.type,
    );
  }
  return active instanceof HTMLElement && active.isContentEditable;
}

/**
 * Keeps a fixed element on the visual viewport while the on-screen keyboard is
 * open, and gives it back to the stylesheet when it is not.
 *
 * iPhone Safari never resizes the layout viewport for the keyboard: it shrinks
 * the visual one and pans the page under it, so an element fixed to the bottom
 * of the layout viewport ends up behind the keyboard, or floating above a gap.
 * Following the visual viewport's top and height puts the element's bottom on
 * the keyboard. Android Chrome with `interactive-widget=resizes-content` (set in
 * index.html) resizes the layout viewport as well, and this agrees with it.
 *
 * The size is held only while the keyboard is open. Request of 6 October 2026:
 * on an iPhone a sheet stayed cut off after the keyboard closed, the part the
 * keyboard had covered never coming back. The phone does not always say the
 * viewport grew again, so a size written while the keyboard was up was left on
 * the sheet. Two things mend it: the keyboard counts as open only while a field
 * that is typed into holds the focus, and whenever it is not open the inline
 * size is removed, so the element is whatever its stylesheet says, the whole
 * screen. The focus leaving a field is looked at again a few times, because
 * that is the one signal an iPhone always sends.
 *
 * Returns whether the keyboard is open, so the home indicator's padding can be
 * dropped while it is: the keyboard covers the indicator, and keeping the
 * padding is what left a strip between the keyboard and the composer.
 */
export function useVisualViewport(target: RefObject<HTMLElement | null>, onResize?: () => void): boolean {
  const [keyboard, setKeyboard] = useState(false);

  useEffect(() => {
    const viewport = window.visualViewport;
    if (viewport === null || viewport === undefined) return;
    const timers = new Set<ReturnType<typeof setTimeout>>();

    const fit = () => {
      const open = typing() && window.innerHeight - viewport.height > KEYBOARD_PX;
      const node = target.current;
      if (node !== null) {
        if (open) {
          node.style.top = `${String(viewport.offsetTop)}px`;
          node.style.height = `${String(viewport.height)}px`;
          node.style.bottom = 'auto';
        } else {
          node.style.top = '';
          node.style.height = '';
          node.style.bottom = '';
        }
      }
      setKeyboard(open);
      onResize?.();
    };

    const settle = () => {
      for (const delay of SETTLE_MS) {
        const timer = setTimeout(() => {
          timers.delete(timer);
          fit();
        }, delay);
        timers.add(timer);
      }
    };

    fit();
    viewport.addEventListener('resize', fit);
    viewport.addEventListener('scroll', fit);
    window.addEventListener('resize', fit);
    window.addEventListener('orientationchange', settle);
    document.addEventListener('focusin', settle);
    document.addEventListener('focusout', settle);
    return () => {
      viewport.removeEventListener('resize', fit);
      viewport.removeEventListener('scroll', fit);
      window.removeEventListener('resize', fit);
      window.removeEventListener('orientationchange', settle);
      document.removeEventListener('focusin', settle);
      document.removeEventListener('focusout', settle);
      timers.forEach((timer) => {
        clearTimeout(timer);
      });
    };
    // The callback is read on each resize; re-subscribing for each render would not help.
  }, [target]);

  return keyboard;
}
